import type {GeoRefAddress} from "@reva/shared/geo-reference";

import type {AddressAmbiguousData} from "../../redux/addressSlice";
import {initializePersistence} from "../../redux/persistenceThunk";
import {useRevaDispatch} from "../../redux/revaHooks";
import {georefAddressStableKey} from "../../util/componentUtil";
import AddressCard from "../address/AddressCard";

interface AmbiguousAddressesProps {
    /** ambiguous addresses from result */
    ambiguousAddresses: AddressAmbiguousData | null;
    /** the currently highlighted address candidate (marked active), or null */
    highlightedAddress: GeoRefAddress | null;
    /** function to highlight one of the addresses */
    highlightAmbiguousAddress: (address: GeoRefAddress) => void;
}

/**
 * Selection list for address disambiguation.
 */
export function AmbiguousAddresses({
    ambiguousAddresses,
    highlightedAddress,
    highlightAmbiguousAddress,
}: AmbiguousAddressesProps) {
    const dispatch = useRevaDispatch();

    const onSelectAddress = (addressCandidate: GeoRefAddress) => {
        // #18907: also run the duplicate check when the address was picked from the disambiguation
        // list — existing valuations at the chosen address open the duplicate dialog first (the check
        // is the default behavior of initializePersistence).
        void dispatch(initializePersistence(addressCandidate));
    };

    if (!ambiguousAddresses) return null;

    return ambiguousAddresses.addresses.map((addressCandidate) => (
        <div key={georefAddressStableKey(addressCandidate)} className="w-full mb-4">
            <AddressCard
                addressMeta={addressCandidate}
                className={
                    "cursor-pointer hover:bg-surface-300 dark:hover:bg-surface-500" +
                    (addressCandidate === highlightedAddress ? " active" : "")
                }
                onSelectAddress={onSelectAddress}
                onHoverAddress={highlightAmbiguousAddress}
            />
        </div>
    ));
}
